import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { normalize } from 'normalizr';
import {
  getEditorSaveDataPromise,
  getReadyStatusPromise,
} from '../../components/editor/defaultOptions.js';
import AddChangeImpactTypeFormModal from '../../components/projects/ChangeImpact/AddChangeImpactTypeFormModal/index.jsx';
import {
  isDateTodayOrAfter,
  mapFinalFormErrors,
  momentToDateString,
  stringToMoment,
} from '../../lib/utils.js';
import ChangeImpactForm from '../../components/projects/ChangeImpact/ChangeImpactForm.jsx';
import {
  createChangeImpact,
  deleteChangeImpact,
  retrieveChangeImpact,
  selectChangeImpact,
  selectRetrieveChangeImpactState,
  updateChangeImpact,
} from '../../modules/changeImpacts.js';
import {
  retrieveChangeImpactTypes,
  selectChangeImpactTypesWithArchived,
  updateChangeImpactTypes,
} from '../../modules/changeImpactTypes.js';
import { selectOrganization } from '../../modules/organizations.js';
import GroupFormContainer from '../organizations/GroupFormContainer.jsx';
import ChangeImpactHeader from '../../components/headers/PageHeaders/Project/ChangeImpactHeader.jsx';
import {
  retrieveProject,
  selectProject,
  selectRetrieveProjectState,
} from '../../modules/projects.js';
import {
  retrieveSimplifiedEmployees,
  selectRetrieveSimplifiedEmployeesState,
} from '../../modules/employees.js';
import SubHeader from '../../components/SubHeader.jsx';
import {
  changeImpactValidate,
  deleteGroupMsg,
  deleteOwnerMsg,
  deleteUngroupedEmployeeMsg,
  onImpactedUploadChange,
  onStakeholdersUploadChange,
} from './utils/index.js';
import {
  retrieveOrgStakeholders,
  selectOrgStakeholders,
  selectOrgStakeholdersWithArchived,
  selectRetrieveOrgStakeholdersState,
} from '../../modules/orgStakeholders.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import { retrieveOrgGroups } from '../../modules/orgGroups.js';
import { formParticipantSchema } from '../../lib/schema.js';
import ManageGroupModal from '../../components/manage-group-modal/ManageGroupModal.jsx';
import ManageParticipantsModal from '../../components/manage-participants-modal/ManageParticipantsModal.jsx';
import EmployeeFormContainer from '../employees/EmployeeFormContainer.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const defaultValues = {
  start_date: null,
  end_date: null,
};

const mapDispatchToProps = {
  createChangeImpact,
  deleteChangeImpact,
  updateChangeImpact,
  retrieveOrgGroups,
  retrieveChangeImpactTypes,
  retrieveChangeImpact,
  retrieveProject,
  retrieveOrgStakeholders,
  retrieveSimplifiedEmployees,
  updateChangeImpactTypes,
};

const mapStateToProps = (state, ownProps) => {
  const editing = typeof ownProps.changeImpactId !== 'undefined';
  return {
    projectId: ownProps.projectId,
    project: selectProject(ownProps.projectId)(state),
    changeImpactId: ownProps.changeImpactId,
    changeImpact: selectChangeImpact(ownProps.changeImpactId)(state),
    changeImpactTypes: selectChangeImpactTypesWithArchived(state),
    organization: selectOrganization(state),
    editing,
    orgStakeholders: editing
      ? selectOrgStakeholdersWithArchived(state)
      : selectOrgStakeholders(state),
    retrieveOrgStakeholdersSuccess:
      selectRetrieveOrgStakeholdersState(state).success,
    retrieveProjectSuccess: selectRetrieveProjectState(state).success,
    retrieveChangeImpactSuccess: selectRetrieveChangeImpactState(state).success,
    retrieveEmployeesPending:
      selectRetrieveSimplifiedEmployeesState(state).pending,
    apiUrl: state.config.apiUrl,
  };
};

/* eslint-disable camelcase */ // this form uses formDate.

function ChangeImpactFormContainer(props) {
  const {
    projectId,
    project,
    changeImpactTypes,
    createChangeImpact,
    deleteChangeImpact,
    changeImpact,
    changeImpactId,
    updateChangeImpact,
    retrieveChangeImpact,
    retrieveProject,
    retrieveProjectSuccess,
    organization,
    retrieveOrgStakeholders,
    retrieveOrgGroups,
    retrieveChangeImpactTypes,
    orgStakeholders,
    updateChangeImpactTypes,
    retrieveChangeImpactSuccess,
    retrieveEmployeesPending,
    retrieveOrgStakeholdersSuccess,
    editing,
    apiUrl,
  } = props;
  const formRef = useRef(null);

  const { downloadBlob } = useFetchFile();

  const initialValue = useMemo(
    () =>
      changeImpact
        ? {
            ...changeImpact,
            start_date: stringToMoment(
              changeImpact.start_date || changeImpact.startDate
            ),
            end_date: stringToMoment(
              changeImpact.end_date || changeImpact.endDate
            ),
            // Form is editing if changeImpactId is provided
          }
        : defaultValues,
    [retrieveChangeImpactSuccess, retrieveProjectSuccess]
  );

  const [editableGroupId, setEditableGroupId] = useState(null);

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  const [showManageGroupModal, setShowManageGroupModal] = useState(false);
  const [showManageParticipantsModal, setShowManageParticipantsModal] =
    useState(false);

  // Stakeholders upload state
  const [uploadStakeholdersMethod, setUploadStakeholdersMethod] =
    useState('manually');
  const [replaceStakeholdersMethod, setReplaceStakeholdersMethod] =
    useState('add');

  const [uploadImpactedMethod, setUploadImpactedMethod] = useState('manually');
  const [replaceImpactedMethod, setReplaceImpactedMethod] = useState('add');

  const [currentStateEditorInstance, setCurrentStateEditorInstance] =
    useState(null);
  const [futureStateEditorInstance, setFutureStateEditorInstance] =
    useState(null);
  const [actionPlanEditorInstance, setActionPlanEditorInstance] =
    useState(null);
  const [showCreateCITypeModal, setCreateCITypeModal] = useState(false);

  const onShowEmployeeModal = () => setShowEmployeeModal(true);
  const onHideEmployeeModal = () => setShowEmployeeModal(false);
  const onHideGroupModal = () => setShowGroupModal(false);
  const onShowGroupModal = () => setShowGroupModal(true);
  const onHideManageParticipantsModal = () =>
    setShowManageParticipantsModal(false);
  const onShowManageParticipantsModal = (id) =>
    setShowManageParticipantsModal(true);
  const onHideManageGroupModal = () => setShowManageGroupModal(false);
  const onShowManageGroupModal = (id) => {
    setEditableGroupId(id);
    setShowManageGroupModal(true);
  };

  const [entities, setEntities] = useState({
    externalEntities: [],
    owners: [],
    groups: {},
    ungroupedEmployees: [],
    groupStakeholders: [],
    individuals: [],
  });

  const updateEntities = (payload) => {
    setEntities({
      ...entities,
      ...payload,
    });
  };

  const onSelectOwner = (e) => {
    const isSelected = entities.owners.some((o) => o.id === e.id);

    if (isSelected) {
      if (!window.confirm(deleteOwnerMsg)) return;
      updateEntities({ owners: entities.owners.filter((o) => o.id !== e.id) });
    } else {
      updateEntities({ owners: [...entities.owners, e] });
    }
  };

  const onAddEmployeeSubmitSuccess = ({ entities, result }) => {
    onSelectOwner(entities.employees[result]);
  };

  const reAssignUngrouped = ({ id, name, employee_ids }) => {
    const commonIds = entities.ungroupedEmployees.filter((id) =>
      employee_ids.includes(id)
    );
    const restUngrouped = entities.ungroupedEmployees.filter(
      (id) => !commonIds.includes(id)
    );
    updateEntities({
      ungroupedEmployees: restUngrouped,
      groups: {
        ...entities.groups,
        [String(id)]: {
          id,
          name,
          employee_ids: commonIds,
        },
      },
    });
  };

  const onSelectGroup = (group) => {
    if (group.id in entities.groups) {
      if (!window.confirm(deleteGroupMsg)) return;

      const { [String(group.id)]: _, ...newObject } = entities.groups;
      updateEntities({ groups: newObject });
    } else {
      updateEntities({
        groups: {
          ...entities.groups,
          [String(group.id)]: {
            id: group.id,
            name: group.name,
            employee_ids: [],
          },
        },
      });
      reAssignUngrouped({ ...group, employee_ids: group.employeeIds });
    }
  };

  const onSelectAllGroupEmployees = (id, employee_ids) => {
    updateEntities({
      groups: {
        ...entities.groups,
        [String(id)]: {
          ...entities.groups[id],
          employee_ids,
        },
      },
    });
  };

  const onSelectUngroupedEmployee = (e) => {
    const isActive = entities.ungroupedEmployees.includes(e.id);
    if (isActive) {
      if (!window.confirm(deleteUngroupedEmployeeMsg)) return;
      updateEntities({
        ungroupedEmployees: entities.ungroupedEmployees.filter(
          (i) => i !== e.id
        ),
      });
    } else {
      updateEntities({
        ungroupedEmployees: [...entities.ungroupedEmployees, e.id],
      });
    }
  };

  const onUpdateExternal = (externals) =>
    updateEntities({ externalEntities: externals });

  const onSubmitManageGroup = (group) =>
    updateEntities({
      groups: { ...entities.groups, [editableGroupId]: group },
    });

  const onSubmitManageUngrouped = (employees) =>
    updateEntities({ ungroupedEmployees: employees });

  const onAddGroupSubmitSuccess = ({ entities, result }) => {
    const query = editing
      ? { objectType: 'change_impact', objectId: changeImpactId }
      : { active: true };

    retrieveOrgGroups(query).then(() =>
      onSelectGroup(entities.orgGroups[result])
    );
  };

  const participantsEditAllowed = () =>
    editing && initialValue?.end_date
      ? isDateTodayOrAfter(initialValue.end_date)
      : true;

  const onDownloadCsv = (type) => {
    // 'stakeholders' || 'impacted'
    const url =
      type === 'impacted' ? 'group_structure_csv' : 'stakeholder_structure_csv';
    const fileName =
      type === 'impacted'
        ? 'groups-and-people-structure_csv'
        : 'stakeholders-structure';
    downloadBlob(
      apiUrl,
      `change_impacts/${changeImpact.id}/${url}`,
      null,
      `current-${fileName}`,
      'csv'
    );
  };

  const onSubmit = (values) => {
    const formData = new FormData(formRef.current);

    const mergedStakeholdersData = [
      ...entities.groupStakeholders,
      { id: null, individuals: entities.individuals },
    ];

    const mergedGroupsData = [
      ...Object.values(entities.groups),
      { id: null, employee_ids: entities.ungroupedEmployees },
    ];
    const composedData = (editorData) => {
      const composedFormData = new FormData();

      const data = {
        title: values.title,
        change_impact_type_id: values.change_impact_type_id,
        people_impacted: values.people_impacted
          ? Math.abs(values.people_impacted)
          : null,
        scale: values.scale,
        perception: values.perception,
        action_plan_types: values.action_plan_types?.length
          ? values.action_plan_types
          : null,
        action_plan_status: values.action_plan_status || null,
        action_plan: editorData[2],
        future_state: editorData[1],
        current_state: editorData[0],
        start_date: momentToDateString(values.start_date),
        end_date: momentToDateString(values.end_date),
        stakeholders_data: mergedStakeholdersData,
        groups_data: mergedGroupsData,
        impacted_external_entities: entities.externalEntities,
        owner_ids: entities.owners.map((e) => e.id),
        manually_stakeholders: uploadStakeholdersMethod === 'manually',
      };

      for (const key in data) {
        composedFormData.append(
          `change_impact[${key}]`,
          JSON.stringify(data[key])
        );
      }

      if (uploadStakeholdersMethod === 'csv') {
        composedFormData.append(
          'change_impact[csv_stakeholders_file]',
          formData.get('csv_stakeholders_file')
        );
        composedFormData.append(
          'stakeholders_merge',
          replaceStakeholdersMethod === 'add'
        );
      }

      if (uploadImpactedMethod === 'csv') {
        composedFormData.append(
          'change_impact[groups_structure_csv]',
          formData.get('groups_structure_csv')
        );
        composedFormData.append(
          'groups_merge',
          replaceImpactedMethod === 'add'
        );
      }
      return composedFormData;
    };
    return Promise.all([
      getReadyStatusPromise(currentStateEditorInstance),
      getReadyStatusPromise(futureStateEditorInstance),
      getReadyStatusPromise(actionPlanEditorInstance),
    ]).then(() =>
      Promise.all([
        getEditorSaveDataPromise(currentStateEditorInstance),
        getEditorSaveDataPromise(futureStateEditorInstance),
        getEditorSaveDataPromise(actionPlanEditorInstance),
      ]).then((editorData) => {
        const submitPromise = changeImpact
          ? updateChangeImpact(composedData(editorData), changeImpact.id)
          : createChangeImpact(projectId, composedData(editorData));
        return submitPromise
          .then(({ result }) =>
            pushRoute('projects/change-impacts/show', {
              projectId,
              changeImpactId: result,
            })
          )
          .catch(mapErrors);
      })
    );
  };

  const onDelete = () => {
    if (window.confirm('Are you sure you want to delete this change impact?')) {
      const submitPromise = deleteChangeImpact(changeImpact.id);

      submitPromise
        .then(() => pushRoute('projects/change-impacts', { projectId }))
        .catch(mapErrors);
    }
  };

  const validate = (values) => changeImpactValidate(values, entities.owners);

  useEffect(() => {
    if (changeImpact?.groups_data) {
      const {
        impacted_external_entities,
        owners,
        groups_data,
        stakeholders_data,
      } = changeImpact;

      const filteredGroups = groups_data.filter((g) => g.id !== null);
      const ungrouped = groups_data.find((g) => g.id === null);
      const normalizedGroups =
        normalize(filteredGroups, [formParticipantSchema]).entities.groups ||
        {};
      const ungroupedEmployees = ungrouped ? ungrouped.employee_ids : [];
      const withoutParent = stakeholders_data.find((s) => s.id === null);

      updateEntities({
        owners,
        externalEntities: impacted_external_entities, // No need for map
        groups: normalizedGroups,
        ungroupedEmployees,
        groupStakeholders: stakeholders_data.filter((s) => s.id !== null),
        ...(withoutParent && { individuals: withoutParent.individuals }),
      });
    }
  }, [retrieveChangeImpactSuccess]);

  useEffect(() => {
    editing && retrieveChangeImpact(changeImpactId, true);
    retrieveProject(projectId);
    retrieveChangeImpactTypes();
    retrieveOrgStakeholders();
    editing &&
      retrieveOrgGroups({
        objectType: 'change_impact',
        objectId: changeImpactId,
      });
    !editing && retrieveOrgGroups({ active: true });
  }, []);

  return (
    <React.Fragment>
      {retrieveProjectSuccess &&
        retrieveOrgStakeholdersSuccess &&
        (editing ? retrieveChangeImpactSuccess : true) && (
          <React.Fragment>
            <ChangeImpactHeader changeImpact={changeImpact} project={project} />
            <SubHeader flexJustify small>
              <h2>{editing ? 'Edit Change Impact' : 'Add Change Impact'}</h2>
              {editing && (
                <Button className="m-l-12" bsStyle="danger" onClick={onDelete}>
                  Delete
                </Button>
              )}
            </SubHeader>

            <div
              style={{
                position: 'relative',
                opacity: retrieveEmployeesPending ? 0.5 : 1,
                cursor: retrieveEmployeesPending ? 'wait' : 'auto',
              }}
            >
              <div>
                <Grid>
                  <Row>
                    <Col sm={12}>
                      <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
                        <Row>
                          <Col sm={10} smOffset={1}>
                            <Form
                              formRef={formRef}
                              initialValues={initialValue}
                              entities={entities}
                              onSubmit={onSubmit}
                              editing={editing}
                              organization={organization}
                              projectId={projectId}
                              validate={validate}
                              orgStakeholders={orgStakeholders}
                              changeImpactTypes={changeImpactTypes}
                              uploadStakeholdersMethod={
                                uploadStakeholdersMethod
                              }
                              setUploadStakeholdersMethod={(m) =>
                                onStakeholdersUploadChange(
                                  m,
                                  setUploadStakeholdersMethod
                                )
                              }
                              setReplaceStakeholdersMethod={
                                setReplaceStakeholdersMethod
                              }
                              replaceStakeholdersMethod={
                                replaceStakeholdersMethod
                              }
                              setUploadImpactedMethod={(m) =>
                                onImpactedUploadChange(
                                  m,
                                  setUploadImpactedMethod
                                )
                              }
                              setReplaceImpactedMethod={
                                setReplaceImpactedMethod
                              }
                              uploadImpactedMethod={uploadImpactedMethod}
                              replaceImpactedMethod={replaceImpactedMethod}
                              ungroupedSearchParams={{
                                excludingGroupIds: Object.keys(entities.groups),
                              }}
                              showCreateCITypeModal={showCreateCITypeModal}
                              setCreateCITypeModal={setCreateCITypeModal}
                              updateEntities={updateEntities}
                              participantsEditAllowed={participantsEditAllowed()}
                              onDownloadCsv={onDownloadCsv}
                              onSelectOwner={onSelectOwner}
                              onSelectGroup={onSelectGroup}
                              onSelectAllGroupEmployees={
                                onSelectAllGroupEmployees
                              }
                              onSelectUngroupedEmployee={
                                onSelectUngroupedEmployee
                              }
                              onUpdateExternal={onUpdateExternal}
                              onShowManageGroupModal={onShowManageGroupModal}
                              onShowManageParticipantsModal={
                                onShowManageParticipantsModal
                              }
                              onShowGroupModal={onShowGroupModal}
                              onShowEmployeeModal={onShowEmployeeModal}
                              setCurrentStateEditorInstance={
                                setCurrentStateEditorInstance
                              }
                              setFutureStateEditorInstance={
                                setFutureStateEditorInstance
                              }
                              setActionPlanEditorInstance={
                                setActionPlanEditorInstance
                              }
                              component={ChangeImpactForm}
                            />
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Grid>
              </div>
            </div>
          </React.Fragment>
        )}

      <ManageGroupModal
        showFormModal={showManageGroupModal}
        onHideFormModal={onHideManageGroupModal}
        group={entities.groups[editableGroupId]}
        onSubmit={onSubmitManageGroup}
        additionalSearchParams={{
          objectType: 'change_impact',
          objectId: changeImpactId,
        }}
      />

      <ManageParticipantsModal
        employeeIds={entities.ungroupedEmployees}
        showFormModal={showManageParticipantsModal}
        onHideFormModal={onHideManageParticipantsModal}
        additionalSearchParams={{
          excludingGroupIds: Object.keys(entities.groups),
          objectType: 'change_impact',
          objectId: changeImpactId,
        }}
        onSubmit={onSubmitManageUngrouped}
        title="Impacted People"
      />

      <GroupFormContainer
        showFormModal={showGroupModal}
        onHideFormModal={onHideGroupModal}
        onSubmitSuccess={onAddGroupSubmitSuccess}
      />

      <AddChangeImpactTypeFormModal
        show={showCreateCITypeModal}
        changeImpactTypes={changeImpactTypes}
        onHide={() => setCreateCITypeModal(false)}
        updateChangeImpactTypes={updateChangeImpactTypes}
        retrieveChangeImpactTypes={retrieveChangeImpactTypes}
      />
      <EmployeeFormContainer
        showFormModal={showEmployeeModal}
        onHideFormModal={onHideEmployeeModal}
        onSubmitSuccess={onAddEmployeeSubmitSuccess}
      />
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeImpactFormContainer);
